import { capitalCase } from 'change-case';
import find from 'lodash/find.js';

import Model from '@/models/Model.js';

export default class AutomatedSurveyTemplate extends Model {
    static entity = 'automated_survey_templates';

    static modelName = 'AutomatedSurveyTemplate';

    static fields() {
        return {
            id: this.attr(null),
            course_template_id: this.attr(null),
            survey_id: this.attr(null),
            group_id: this.attr(null),
            triggered_by: this.attr(null),
            triggerable_after_seconds: this.attr(null),
            triggerable_id: this.attr(null),
            triggerable_type: this.attr(null),
            triggerable_type_entity: this.attr(null),
            subject_id: this.attr(null),
            subject_type: this.attr(null),
            subject_type_entity: this.attr(null),
            enrollment_subject: this.attr(null),
            locked_after_seconds: this.attr(null),
            required_after_seconds: this.attr(null),
            is_anonymous: this.attr(null),
            time_limit: this.attr(null),

            ...super.fields(),

            course_template: this.belongsTo('course_templates', 'course_template_id'),
            survey: this.belongsTo('quizzes', 'survey_id'),
            triggerable: this.morphTo('triggerable_id', 'triggerable_type_entity'),
            subject: this.morphTo('subject_id', 'subject_type_entity'),
            automated_surveys: this.hasMany('automated_surveys', 'automated_survey_template_id'),
        };
    }

    get triggered_by_description() {
        return capitalCase(this.triggered_by);
    }

    get locked_after_display() {
        if (this.locked_after_seconds === null) {
            return '';
        }

        if (this.locked_after_seconds === 0) {
            return 'Immediately';
        }

        return this.fromSecondsToDisplay('locked_after_seconds');
    }

    get required_after_display() {
        if (this.required_after_seconds === null) {
            return '';
        }

        if (this.required_after_seconds === 0) {
            return 'Immediately';
        }

        return this.fromSecondsToDisplay('required_after_seconds');
    }

    get has_time_limit() {
        return !!this.time_limit;
    }

    get is_locked() {
        return this.locked_after_seconds !== null;
    }

    get is_required() {
        return this.required_after_seconds !== null;
    }

    get should_be_anonymous() {
        return this.is_anonymous;
    }

    get time_limit_display() {
        return this.fromSecondsToDisplay('time_limit');
    }

    get subject_type_title() {
        if (!this.subject_type) {
            return '';
        }

        const model = this.subject_type.replace('App\\Models\\', '');

        return capitalCase(model);
    }

    get subject_link() {
        if (this.subject_type === 'App\\Models\\ActivityTemplate') {
            return {
                name: 'activity-template-view',
                params: {
                    courseTemplateId: this.subject.course_template_id,
                    activityTemplateId: this.subject_id,
                },
            };
        }

        if (this.subject_type === 'App\\Models\\Course') {
            return {
                name: 'course-template-dashboard',
                params: {
                    courseTemplateId: this.course_template.id,
                },
            };
        }

        if (this.subject_type === 'App\\Models\\CourseGrade') {
            return {
                name: 'course-template-dashboard',
                params: {
                    courseTemplateId: this.course_template.id,
                },
            };
        }

        return null;
    }

    get subject_title() {
        if (this.subject_type === 'App\\Models\\ActivityTemplate') {
            return this.subject.title;
        }

        if (this.subject_type === 'App\\Models\\Course') {
            return this.course_template.title;
        }

        return this.subject.user.name;
    }

    get enrollment_subject_description() {
        return {
            all: 'All Instructors',
            current_instructors: 'Current Instructors Only',
            past_instructors: 'Past Instructors Only',
        }[this.enrollment_subject];
    }

    surveyVersion(versions) {
        return find(versions, { versionable_id: this.survey_id, versionable_type: 'App\\Models\\Survey' });
    }

    isUsingLatestVersion(versions) {
        const versionUsed = this.surveyVersion(versions);

        if (!versionUsed) {
            throw new Error('Could not identify which survey version is being used.');
        }

        return versionUsed.is_latest;
    }

    toForm() {
        return {
            survey_id: this.survey_id,
            triggered_by: this.triggered_by,
            triggerable_after_seconds: this.triggerable_after_seconds,
            triggerable_id: this.triggerable_id,
            subject_type: this.subject_type,
            subject_id: this.subject_id,
            enrollment_subject: this.enrollment_subject,
            locked_after_seconds: this.locked_after_seconds,
            required_after_seconds: this.required_after_seconds,
            is_anonymous: this.is_anonymous,
            time_limit: this.time_limit,
        };
    }
}
